import { AnalyticCategories, AnalyticService } from '@application/analytic/analytic.service';
import { useFeatureFlag } from '@application/feature_flags/use_feature_flag';
import { useQueryClient } from '@tanstack/react-query';
import { clientIdentityGetOptions, clientIdentityInviteablePersonsOptions } from '@visiba/api-vcs-client-v2';
import { useResolve } from '@visiba/instantiation';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { BookingScreen, useBooking, ViewBookingAnalyticActions } from '../../hooks/use_booking';
import { useBookingBasket } from '../../hooks/use_booking_basket';

interface PersonViewModel {
	identityNumber: string;
	identityCode: string;
	name: string;
	isMe: boolean;
	isSubject: boolean;
}

interface PresentationModel {
	persons: PersonViewModel[];
	selectedIdentityNumbers: string[];
}

interface FormData {
	access: string[];
}

export function useAccess() {
	const viewBooking = useBooking();
	const { getBasket, setBasket } = useBookingBasket();
	const analyticService = useResolve(AnalyticService);
	const featureUseRepresentatives = useFeatureFlag('PatientWeb.UseRepresentatives');
	const queryClient = useQueryClient();
	const [persons, setPersons] = useState<PersonViewModel[]>([]);
	const [status, setStatus] = useState<'pending' | 'accessible' | 'error'>('pending');

	const data = useMemo<PresentationModel>(() => {
		const basket = getBasket();
		const selectedNumbers = basket.additionalParticipants?.map((p) => p.IdentityNumber) ?? [];
		return {
			persons,
			selectedIdentityNumbers: selectedNumbers,
		};
	}, [persons, getBasket]);

	useEffect(() => {
		analyticService.trackPageView({
			customTitle: 'Booking: Who has access',
		});
	}, [analyticService]);

	useEffect(() => {
		if (!featureUseRepresentatives) {
			analyticService.trackEvent(AnalyticCategories.Booking, ViewBookingAnalyticActions.DoesNotUseRepresentatives);
			viewBooking.goToScreen(BookingScreen.Conclusion, { replace: true });
			return;
		}

		(async () => {
			try {
				const [inviteableResult, identityResult] = await Promise.all([
					queryClient.fetchQuery(clientIdentityInviteablePersonsOptions()),
					queryClient.fetchQuery(clientIdentityGetOptions()),
				]);
				const inviteablePersons = inviteableResult.Data ?? [];
				const identity = identityResult.Data;
				const myIdentityNumber = identity?.NationalIdentityNumber;

				// The currently represented person (e.g. a child) is the booking subject. The backend
				// always adds them as the main subject, so they must never be sent as an additional
				// participant — only the other inviteable guardians can be invited.
				const represented =
					identity?.RepresentingId != null ? identity.CanRepresent?.find((p) => p.Id === identity.RepresentingId) : undefined;
				const subjectIdentityNumber = represented?.IdentityNumber;

				const personViewModels: PersonViewModel[] = inviteablePersons.map((p) => ({
					identityNumber: p.IdentityNumber,
					identityCode: p.IdentityCode,
					name: p.Name,
					isMe: p.IdentityNumber === myIdentityNumber,
					isSubject: subjectIdentityNumber != null && p.IdentityNumber === subjectIdentityNumber,
				}));

				// When the actor logs in as themselves (e.g. a child logging in directly), the
				// inviteable list returns the people who can represent them (their legal guardians) but
				// not the actor themselves. Surface the actor as a readonly "me" entry so they appear
				// in the access step alongside their guardians, mirroring the parent-books-for-child flow.
				if (
					identity?.RepresentingId == null &&
					myIdentityNumber != null &&
					identity?.Nationality != null &&
					!personViewModels.some((p) => p.isMe) &&
					personViewModels.length > 0
				) {
					const meName = [identity.FirstName, identity.LastName].filter(Boolean).join(' ');
					personViewModels.unshift({
						identityNumber: myIdentityNumber,
						identityCode: identity.Nationality,
						name: meName,
						isMe: true,
						isSubject: false,
					});
				}

				// Add the represented subject if not already in the inviteable list, so it is shown
				// (read-only) as always having access.
				if (represented && !personViewModels.some((p) => p.identityNumber === represented.IdentityNumber)) {
					const name = [represented.GivenName, represented.Surname].filter(Boolean).join(' ') || represented.CommonName;
					personViewModels.push({
						identityNumber: represented.IdentityNumber,
						identityCode: represented.IdentityCode,
						name,
						isMe: false,
						isSubject: true,
					});
				}

				setPersons(personViewModels);
				setStatus('accessible');

				if (personViewModels.length === 0) {
					setBasket({ additionalParticipants: [] });
					viewBooking.goToScreen(BookingScreen.Conclusion, { replace: true });
				}
			} catch {
				setStatus('error');
				viewBooking.goToScreen(BookingScreen.Error);
			}
		})();
	}, [analyticService, featureUseRepresentatives, queryClient, viewBooking, setBasket]);

	const submit = useCallback(
		(formData: FormData) => {
			const selected = new Set(formData.access);

			const additionalParticipants = persons
				.filter((person) => !person.isMe && !person.isSubject && selected.has(person.identityNumber))
				.map((person) => ({
					IdentityCode: person.identityCode,
					IdentityNumber: person.identityNumber,
				}));

			setBasket({ additionalParticipants });
			viewBooking.goToScreen(BookingScreen.Conclusion);
		},
		[persons, setBasket, viewBooking],
	);

	return { data, status, submit };
}
